import { InfoCircleOutlined } from '@ant-design/icons'
import { Descriptions, Drawer, Tooltip } from 'antd'
import { useAtom } from 'jotai'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getPointPermission, MosaicComponent, UserAvatar } from '@/components'
import { Customer, CustomerFlowStatus, CustomerRole, HistoryRecord } from '@/modules/customer/customer.schema'
import { User, usersAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, Permissions, PriorityEnum } from '@/schemas'

const getBeforeValueContent = (record: HistoryRecord, t: (s: string) => string) => {
  let beforeValue = record.beforeValue
    ? record.type === 'flow'
      ? t(`customer.flowStatus.${record.beforeValue}.title`)
      : record.beforeValue
    : null

  if (beforeValue && ['startDate', 'endDate', 'expectTime', 'expectValidationTime'].includes(record.key)) {
    beforeValue = moment(record.beforeValue).format('YYYY-MM-DD')
  }

  if (record.key === 'priority') {
    beforeValue = t(`form.priority.${PriorityEnum[Number(record.beforeValue)]}`)
  }
  return beforeValue
}

const getAfterValueContent = (record: HistoryRecord, t: (s: string) => string) => {
  let currentValue =
    record.type === 'flow' ? t(`customer.flowStatus.${record.currentValue}.title`) : record.currentValue

  if (currentValue && ['startDate', 'endDate', 'expectTime', 'expectValidationTime'].includes(record.key)) {
    currentValue = moment(record.currentValue).format('YYYY-MM-DD')
  }

  if (record.key === 'priority') {
    currentValue = t(`form.priority.${PriorityEnum[Number(record.currentValue)]}`)
  }
  return currentValue
}

export const HistoryItem: React.FC<{
  record: HistoryRecord
  user?: User
  permissions: Permissions
}> = ({ record, user, permissions }) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)

  const additions = record.additions || []

  // TODO 更细节的字段级别限制需要写个map 进行判断 record.key
  const isChangeField = record.key.match(/ChangeReason$/)
  const name = t(
    record.type === 'flow'
      ? `customer.flowStatus.status.title`
      : isChangeField
        ? `customer.detail.changeReason`
        : `customer.detail.${record.key}`,
    {
      field: isChangeField ? t(`customer.detail.${record.key.replace(/ChangeReason$/, '')}`) : undefined,
    }
  )

  const beforeValue = getBeforeValueContent(record, t)
  const currentValue = getAfterValueContent(record, t)

  return (
    <div className='rounded-md cursor-pointer px-2 pt-1 pb-2 hover:(bg-[#F9F9FC])'>
      <div className='flex mb-1 gap-2 items-center'>
        <span className='font-medium'>{name}</span>
        {!!user && (
          <Tooltip title={user.email}>
            <span className='text-[12px] text-[#8A8A9A]'>{user?.name}</span>
          </Tooltip>
        )}
        <span className='text-[12px] text-[#8A8A9A]'>{moment(record.createdAt).utc().format('YYYY-MM-DD HH:mm')}</span>
      </div>
      <div className='flex text-[12px] gap-2'>
        {!!beforeValue && (
          <div className='bg-[#feeaec] rounded-sm py-0.5 px-2 line-through'>
            {<span style={{ wordBreak: 'break-word' }}>{beforeValue}</span>}
          </div>
        )}
        {!!currentValue && (
          <div className='rounded-sm bg-[#dff8ea] py-0.5 px-2' onClick={() => additions.length > 0 && setOpen(true)}>
            <span style={{ wordBreak: 'break-word' }}>{currentValue}</span>
            {additions.length > 0 ? (
              <Tooltip title={t('customer.review.additionTooltip')}>
                <InfoCircleOutlined className='ml-1 text-[10px]' />
              </Tooltip>
            ) : null}
          </div>
        )}
        <Drawer title={t('customer.review.addition')} placement='right' visible={open} onClose={() => setOpen(false)}>
          <Descriptions
            bordered
            column={1}
            title={
              <span>
                <span className='bg-[#feeaec] line-through'>{beforeValue}</span>
                {beforeValue && <span className='mx-2'>→</span>}
                <span className='bg-[#dff8ea]'>{currentValue}</span>
              </span>
            }
          >
            {additions.map((d) => {
              let label = d.type
              let value = d.value

              if (Object.values(CustomerRole).includes(d.type as any)) {
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, permissions).canRead) {
                  value = (<MosaicComponent />) as any
                } else {
                  value = (<UserAvatar size={20} showName email={value}></UserAvatar>) as any
                }
              }
              let content = <span>{value}</span>

              // 审批意见
              if (d.type === 'reviewComment') {
                label = t('customer.detail.reviewComment')
              }

              if (d.type === 'predictWorkload') {
                label = t('customer.predictWorkload')
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, permissions).canRead) {
                  content = <MosaicComponent />
                }
              }

              if (d.type === 'deliveryTime') {
                label = t('customer.detail.deliveryTime')
                if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付时间, permissions).canRead) {
                  content = <MosaicComponent />
                }
              }

              if (d.type === 'reason') {
                if (record.currentValue === CustomerFlowStatus.研发暂停中) {
                  label = t('customer.flowStatus.DevOnGoing.pending.reason')
                }

                if (record.currentValue === CustomerFlowStatus.研发进行中) {
                  label = t('customer.flowStatus.CustomerWaitForValidate.rejectReason')
                }

                if (record.currentValue === CustomerFlowStatus.项目已取消) {
                  label = t('customer.flowStatus.DevPending.cancel.reason')
                }

                if (record.currentValue === CustomerFlowStatus.项目已失败) {
                  label = t('customer.flowStatus.ProjectFailed.reason')
                }
              }

              if (d.type === 'comment') {
                if (record.currentValue === CustomerFlowStatus.结果追踪中) {
                  label = t('customer.flowStatus.CustomerWaitForValidate.exceedReason')
                }

                if (record.currentValue === CustomerFlowStatus.结果复核中) {
                  label = t('customer.flowStatus.ResultTracking.comment')
                }

                if (record.currentValue === CustomerFlowStatus.待项目结项) {
                  label = t('customer.flowStatus.ResultReviewing.comment')
                }
              }

              if (d.type === 'percentage') {
                if (record.currentValue === CustomerFlowStatus.结果复核中) {
                  label = t('customer.flowStatus.ResultTracking.percentage')
                }
                if (record.currentValue === CustomerFlowStatus.待项目结项) {
                  label = t('customer.flowStatus.ResultReviewing.percentage')
                }

                if (record.currentValue === CustomerFlowStatus.待客户验收) {
                  label = t('customer.flowStatus.DevOnGoing.percentage.title')
                }
              }

              if (d.type === 'description') {
                if (record.currentValue === CustomerFlowStatus.待客户验收) {
                  label = t('customer.flowStatus.DevOnGoing.description.title')
                }
              }

              if (d.type === 'delayReason') {
                label = t('customer.flowStatus.CustomerWaitForValidate.selectReason.title')
                content = <span>{t(`customer.flowStatus.CustomerWaitForValidate.selectReason.${d.value}`)}</span>
              }

              if (d.type === 'actualReleaseTime') {
                label = t('customer.flowStatus.CustomerWaitForValidate.actualReleaseTime.title')
              }

              return (
                <Descriptions.Item key={d.id} label={label}>
                  {content}
                </Descriptions.Item>
              )
            })}
          </Descriptions>
        </Drawer>
      </div>
    </div>
  )
}

export const CustomerReviews: React.FC<{ customer: Customer }> = ({ customer }) => {
  const { historyRecords } = customer
  const [users] = useAtom(usersAtom)

  return (
    <>
      {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_历史记录, customer.permissions).canRead ? (
        historyRecords.map((r) => {
          const user = users.find((u) => u.email === r.email)
          return (
            <HistoryItem user={user} record={r} key={`${r.key}_${r.createdAt}`} permissions={customer.permissions} />
          )
        })
      ) : (
        <MosaicComponent />
      )}
    </>
  )
}
